import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { basename } from '@/lib/desktop-paths';
import { toolActivitySummary } from './tool-activity';
import { browserToolTitle, codeToolTitle, dynamicToolAction, toolDetail } from './tool-identity';
import type { ToolActivityPart } from './types';

/** The agent's side of the latest turn: its messages since the user's last one. */
export function latestReply(messages: readonly CrowdexUIMessage[]): CrowdexUIMessage[] {
  const lastUser = messages.findLastIndex((message) => message.role === 'user');
  return messages.slice(lastUser + 1).filter((message) => message.role === 'assistant');
}

/** One step of the agent's, e.g. "Editing button.tsx" or "Running npm test". */
export function toolStepLabel(
  part: Extract<CrowdexUIMessage['parts'][number], { type: 'dynamic-tool' }>,
): string {
  const code = codeToolTitle(part);
  if (code !== null) return code;
  const browser = browserToolTitle(part.toolName, part.title, part.input);
  if (browser !== null) return browser;
  const detail = toolDetail(part, null);
  // A path reads as its file name; a command or a title stays whole.
  const subject = /^[^\s]*[\\/][^\s]+$/.test(detail) ? basename(detail) : detail;
  return `${dynamicToolAction(part)} ${subject}`;
}

/** One line on what the agent is doing in its latest turn, e.g. "Editing button.tsx". */
export function turnActivity(messages: readonly CrowdexUIMessage[]): string {
  const part = latestReply(messages)
    .at(-1)
    ?.parts.findLast(
      (candidate) =>
        candidate.type === 'text' ||
        candidate.type === 'reasoning' ||
        candidate.type === 'dynamic-tool',
    );
  if (part?.type === 'dynamic-tool') return toolStepLabel(part);
  if (part?.type === 'reasoning') return 'Thinking';
  if (part?.type === 'text') return 'Writing';
  return 'Working';
}

/** The last thing the agent said in its latest turn. */
export function latestReplyText(messages: readonly CrowdexUIMessage[]): string | null {
  const part = latestReply(messages)
    .flatMap((message) => message.parts)
    .findLast((candidate) => candidate.type === 'text' && candidate.text.trim().length > 0);
  return part?.type === 'text' ? part.text : null;
}

/** What the agent did in its latest turn, in one line, e.g. "Edited 2 files, 1 command". */
export function latestReplySteps(messages: readonly CrowdexUIMessage[]): string | null {
  const parts = latestReply(messages)
    .flatMap((message) => message.parts)
    .filter(
      (part): part is ToolActivityPart =>
        part.type === 'dynamic-tool' || part.type === 'data-diff' || part.type === 'data-terminal',
    );
  if (parts.length === 0) return null;
  const summary = toolActivitySummary(parts);
  return `${summary.action} ${summary.details}`;
}
